import type { GenerateBuildingFeatureSet } from "building-types/building/buildingTypes";
import type { CipComponentEntity } from "building-types/floors/cipComponentTypes";
import { type MasonryComponentEntity } from "building-types/floors/masonryComponentTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { HomeSettings } from "building-types/settings/homeSettings";
import type { RailingDefaultSetting } from "building-types/settings/railingTypes";
import type { WallCatalogSettings } from "building-types/settings/wallCatalogSettings";
import type { Table } from "common/data/table";
import { getRailingCatalog } from "doc/elements/railing/defaultRailingCatalog";
import { computeWallSettingsOffsets } from "doc/utils/wallTypeTools";
import { wallPlaneAssemblyComponentToLegacyStructuralMaterial } from "../elements/walls2/wallPlaneAssemblyLegacyConversionTools";

/**
 * Some Settings have dynamically generated fields that are not user accessible
 * @param finalSettings the HomeSettings that will flow to generateBuilding
 * @returns
 */
export function finalizeSettings(
  finalSettings: HomeSettings,
  sheathingComponents: Table<SheathingComponentEntity>,
  framingComponents: Table<FramingComponentEntity>,
  masonryComponents: Table<MasonryComponentEntity>,
  cipComponents: Table<CipComponentEntity>,
  railingCatalogEntries: Table<RailingDefaultSetting> | undefined,
  enabledFeatures?: GenerateBuildingFeatureSet,
): HomeSettings {
  const wall = computeWallSettingsOffsets(finalSettings.wall, finalSettings.wallCatalog);

  // HomeSettings.profile stays a pure settings object — doc.profiles is never merged in here.
  // When settingsToCatalogs is on, doc.profiles is the exclusive profile source and is resolved
  // separately by getActiveProfileTable/getActiveProfileDescriptor at each lookup site. See
  // packages/building-generation/elements/profileTools.ts.
  const wallCatalog = enabledFeatures?.wallPlanes
    ? computeWallCatalogSettingsFromComponents(
        finalSettings.wallCatalog,
        sheathingComponents,
        framingComponents,
        masonryComponents,
        cipComponents,
      )
    : finalSettings.wallCatalog;

  const railing = {
    ...finalSettings.railing,
    railingSettingsTable: getRailingCatalog(finalSettings.railing, railingCatalogEntries, enabledFeatures),
  };

  return {
    ...finalSettings,
    wall,
    wallCatalog,
    railing,
  };
}

/**
 * This function computes the WallCatalogSettings from the SheathingComponents and FramingComponents.
 * It adds the SheathingComponents to the WallCatalogSettings as SUBSTRATE materials
 * and the FramingComponents to the WallCatalogSettings as STRUCTURE materials.
 *
 * WALLS V2 @jaggyluis
 *
 * @NOTE - this is to support Wall Planes, since downstream wall generation functions expect
 * the materials to be in the WallCatalogSettings for things like offset calculations and mitering
 *
 * @param wallCatalog the WallCatalogSettings to update
 * @param sheathingComponents the SheathingComponents to add to the WallCatalogSettings
 * @param framingComponents the FramingComponents to add to the WallCatalogSettings
 * @param masonryComponents the MasonryComponents to add to the WallCatalogSettings
 * @param cipComponents the CipComponents to add to the WallCatalogSettings
 * @returns the updated WallCatalogSettings
 */
function computeWallCatalogSettingsFromComponents(
  wallCatalog: WallCatalogSettings,
  sheathingComponents: Table<SheathingComponentEntity>,
  framingComponents: Table<FramingComponentEntity>,
  masonryComponents: Table<MasonryComponentEntity>,
  cipComponents: Table<CipComponentEntity>,
) {
  const wallMaterials = { ...wallCatalog.wallMaterials };

  for (const cid in sheathingComponents) {
    const component = sheathingComponents[cid];
    wallMaterials[cid] = wallPlaneAssemblyComponentToLegacyStructuralMaterial(component);
  }
  for (const cid in framingComponents) {
    const component = framingComponents[cid];
    wallMaterials[cid] = wallPlaneAssemblyComponentToLegacyStructuralMaterial(component);
  }
  for (const cid in masonryComponents) {
    const component = masonryComponents[cid];
    wallMaterials[cid] = wallPlaneAssemblyComponentToLegacyStructuralMaterial(component);
  }
  for (const cid in cipComponents) {
    const component = cipComponents[cid];
    wallMaterials[cid] = wallPlaneAssemblyComponentToLegacyStructuralMaterial(component);
  }

  return {
    ...wallCatalog,
    wallMaterials,
  };
}
